'use strict';

import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { syncCampaignTarget } from 'utils/network/ServiceHelper';
import Notification from 'components/Notification';
import { useNavigate } from 'react-router';
import { Transition } from '@headlessui/react';
import { STATUS_UNAUTHORIZED, SUCCESS_MESSAGE } from 'utils/constants';

export default function CampaignDetail({ data }) {
  const navigate = useNavigate();
  const [notification, setNotification] = useState({
    show: false,
    error: false,
    title: '',
    message: ''
  });
  const [showStore, setShowStore] = useState(false);
  const [search, setSearch] = useState('');

  function openStore() {
    setShowStore(true);
  }

  function closeStore() {
    setShowStore(false);
  }

  function handleChangeForm(e) {
    setSearch(e.target.value);
  }

  function renderFilter() {
    return data?.store?.filter((el) =>
      el.name.toLowerCase().includes(search.toLowerCase())
    );
  }

  function notif(title, message, isError = false, duration = 3000) {
    setNotification({
      show: true,
      title: title,
      message: message,
      error: isError
    });

    setTimeout(() => {
      setNotification({ ...notification, show: false });
    }, duration);
  }

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }

  async function handleSyncCampaignTarget() {
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const res = await syncCampaignTarget(baseUrl, data);

      if (res?.message === SUCCESS_MESSAGE) {
        notif('Success', 'Campaign target successfully synced');
        return;
      }

      if (res?.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const error = JSON.parse(await res.text());
      if (error) {
        notif('Error', error.message, true);
        return;
      }

      notif('Error', 'Network error. Please try again later!', true);
    } catch (e) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  return (
    <div>
      <div className="flex flex-row">
        <h2 className="pt-5 pl-5 font-semibold text-purple-800">
          Campaign Detail
        </h2>
        <div className="flex-1" />
        {data.is_bulk ? (
          <button
            className="px-4 py-2 mt-3 mr-3 text-sm font-medium text-center bg-white border border-yellow-500 rounded-lg text-secondary leading-5 text-purple transition-colors duration-150 active:bg-yellow-600 hover:bg-yellow-100 focus:outline-none focus:shadow-outline-purple"
            onClick={handleSyncCampaignTarget}
          >
            Sync Campaign Target
          </button>
        ) : null}
        <a
          className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
          href={`/campaign-target?id=${data.id}&name=${data.name}&source=${
            data.source
          }&sync=${data.is_bulk ? '1' : '0'}`}
        >
          Campaign Target
        </a>
      </div>
      <div className="px-5 py-2 grid grid-cols-2">
        <div className="py-3 text-sm font-semibold border-b">
          Campaign Source
        </div>
        <div className="py-3 text-sm border-b">{data.source.join(', ')}</div>
        <div className="py-3 text-sm font-semibold border-b">Campaign Name</div>
        <div className="py-3 text-sm border-b">{data.name}</div>
        <div className="py-3 text-sm font-semibold border-b">
          Campaign Title
        </div>
        <div className="py-3 text-sm border-b">{data.title}</div>
        <div className="py-3 text-sm font-semibold border-b">
          Campaign Description
        </div>
        <div className="py-3 text-sm border-b">{data.description}</div>
        <div className="py-3 text-sm font-semibold border-b">
          Campaign Content
        </div>
        <div className="py-3 text-sm border-b">{data.content}</div>
        <div className="py-3 text-sm font-semibold border-b">Image</div>
        <div className="py-3 text-sm border-b">
          {data.image_url.length > 0 ? (
            <img
              src={data.image_url}
              className="object-contain w-24 h-24 rounded"
            />
          ) : (
            '-'
          )}
        </div>
        <div className="py-3 text-sm font-semibold border-b">Banner Image</div>
        <div className="py-3 text-sm border-b">
          {data.banner_image_url.length > 0 ? (
            <img
              src={data.banner_image_url}
              className="object-cover w-full h-24 rounded"
            />
          ) : (
            '-'
          )}
        </div>
        <div className="py-3 text-sm font-semibold border-b">Video URL</div>
        <div className="py-3 text-sm border-b">
          {data.video_url.length > 0 ? <>{data.video_url}</> : '-'}
        </div>
        <div className="py-3 text-sm font-semibold border-b">Deeplink</div>
        <div className="py-3 text-sm border-b">{data.deeplink}</div>
        <div className="py-3 text-sm font-semibold border-b">Link</div>
        <div className="py-3 text-sm border-b">{data?.link || '-'}</div>
        <div className="py-3 text-sm font-semibold border-b">Valid From</div>
        <div className="py-3 text-sm border-b">
          {moment(data.valid_from).format('DD MMMM yyyy HH:mm')}
        </div>
        <div className="py-3 text-sm font-semibold border-b">Valid To</div>
        <div className="py-3 text-sm border-b">
          {moment(data.valid_to).format('DD MMMM yyyy HH:mm')}
        </div>
        {data.store?.length ? (
          <div className="col-span-2 grid grid-cols-2">
            <div className="py-3 text-sm font-semibold border-b">Stores</div>
            <div className="py-3 text-sm border-b">
              <button
                className="px-4 py-2 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                onClick={openStore}
              >
                Show Store List
              </button>
            </div>
          </div>
        ) : null}

        {/* <div className="py-3 text-sm font-semibold border-b">Schedule</div>
        <div className="py-3 text-sm border-b">
          <div className="w-full grid grid-cols-3 gap-3">
            {data.schedule_at.map((x, i) => (
              <span
                key={i}
                className="px-3 py-1 mr-2 text-center border border-yellow-500 rounded text-secondary"
              >
                {moment.utc(x, 'HH:mm').local().format('HH:mm')}
              </span>
            ))}
          </div> 
        </div> */}
      </div>
      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
      {/* Store List */}
      <Transition
        show={showStore}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Store List</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeStore}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <label className="block text-sm mt-3">
                <span className="text-gray-700 dark:text-gray-400">
                  Search Store
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  value={search}
                  onChange={handleChangeForm}
                />
              </label>
              <div className="mt-3">
                {data?.store
                  ? renderFilter().map((x, i) => (
                      <div
                        className="grid grid-cols-10 gap-3"
                        key={i.toString()}
                      >
                        <span>{i + 1}</span>
                        <span className="col-span-9">{x.name}</span>
                      </div>
                    ))
                  : null}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}

CampaignDetail.propTypes = {
  data: PropTypes.any.isRequired,
  handleError: PropTypes.func
};
